import React from 'react';
import { Plus, ArrowUp, Square, Wrench, Terminal, Zap, Bot, BookOpen, GitBranch, Database, ExternalLink, Bug, Palette, Crosshair, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { SHADOWS } from '@/styles/tokens';
import { CommandAutocomplete } from './CommandAutocomplete';
import { MentionAutocomplete } from './MentionAutocomplete';
import { VoiceMicButton } from './VoiceMicButton';
import { PlanModeToggle } from '@/components/ai/plan';
import { useStyleIntelligenceStore } from '@/stores/ai/style-intelligence-store';
import { useEditorStore } from '@/stores/editor/editor-store';
import type { MentionContext, MentionSegment } from '@/services/ai/mentions';
import type { VoiceState } from '@/services/voice/types';
import type { UseDataDiscoveryReturn } from './useDataDiscovery';

export interface ChatInputBarProps {
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  isStreamingActive: boolean;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  placeholder: string;
  showAutocomplete: boolean;
  handleSelectCommand: (command: string) => void;
  showMentionAutocomplete: boolean;
  activeMentionContext: MentionContext | null;
  cursorPosition: number;
  handleSelectMention: (reference: string, segment: MentionSegment | null) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  handleSendMessage: () => Promise<void>;
  handleStopGeneration: () => void;
  voiceState: VoiceState;
  voiceError: string | null;
  handleMicPress: () => void;
  handleStopSpeaking: () => void;
  setConversationPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSkillsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAgentsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDebugSuite: React.Dispatch<React.SetStateAction<boolean>>;
  openKnowledgeModal: () => void;
  setRoadmapModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  canUsePlatformAdminDebug: boolean;
  dataDiscovery: Pick<UseDataDiscoveryReturn, 'openModal'>;
  mission: { openModal: () => void };
  styleIntelligence: { openModal: () => void };
}

export const ChatInputBar: React.FC<ChatInputBarProps> = ({
  inputValue,
  setInputValue,
  isLoading,
  isStreamingActive,
  isFocused,
  setIsFocused,
  inputRef,
  placeholder,
  showAutocomplete,
  handleSelectCommand,
  showMentionAutocomplete,
  activeMentionContext,
  cursorPosition,
  handleSelectMention,
  handleInputChange,
  handleKeyPress,
  handleSendMessage,
  handleStopGeneration,
  voiceState,
  voiceError,
  handleMicPress,
  handleStopSpeaking,
  setConversationPopoverOpen,
  setShowSkillsModal,
  setShowAgentsModal,
  setShowDebugSuite,
  openKnowledgeModal,
  setRoadmapModalOpen,
  canUsePlatformAdminDebug,
  dataDiscovery,
  mission,
  styleIntelligence,
}) => {
  const projectId = useEditorStore(s => s.project?.id);
  const profiles = useStyleIntelligenceStore(s => s.profiles);
  const activeProfile = useStyleIntelligenceStore(s => s.activeProfile);
  const fetchProfiles = useStyleIntelligenceStore(s => s.fetchProfiles);

  React.useEffect(() => {
    fetchProfiles(projectId || undefined);
  }, [fetchProfiles, projectId]);

  const activeStyleProfiles = React.useMemo(() => {
    const orgWideActiveProfile = profiles.find(profile =>
      profile.status === 'active' && (profile.activeProjectIds?.length ?? 0) === 0
    ) || null;
    const projectActiveProfile = projectId
      ? profiles.find(profile => profile.activeProjectIds?.includes(projectId)) || null
      : null;
    const effectiveProfile = projectId
      ? projectActiveProfile || orgWideActiveProfile
      : activeProfile || orgWideActiveProfile;
    return {
      effectiveProfile,
      projectActiveProfile,
      orgWideActiveProfile,
      hasProjectBaseline: Boolean(projectActiveProfile && orgWideActiveProfile),
    };
  }, [activeProfile, profiles, projectId]);

  const activeStyleProfile = activeStyleProfiles.effectiveProfile;
  const activeStyleLabel = activeStyleProfiles.hasProjectBaseline
    ? `Style: ${activeStyleProfiles.projectActiveProfile?.name} + baseline`
    : activeStyleProfile
      ? `Style: ${activeStyleProfile.name}`
      : '';
  const activeStyleAriaLabel = activeStyleProfiles.hasProjectBaseline
    ? `Active project style profile: ${activeStyleProfiles.projectActiveProfile?.name}; org-wide baseline: ${activeStyleProfiles.orgWideActiveProfile?.name}`
    : activeStyleProfile
      ? `Active style profile: ${activeStyleProfile.name}`
      : undefined;
  const activeStyleTooltip = activeStyleProfiles.hasProjectBaseline
    ? `Project style: ${activeStyleProfiles.projectActiveProfile?.name}; baseline: ${activeStyleProfiles.orgWideActiveProfile?.name}`
    : 'Active style profile';

  return (
        <div
          style={{
            border: isFocused ? '1px solid hsl(var(--primary) / 0.7)' : '1px solid hsl(var(--border))',
            borderRadius: '28px',
            backgroundColor: 'hsl(var(--card))',
            boxShadow: SHADOWS.sm,
            transition: 'border-color 150ms ease-in-out',
          }}
        >
          {/* Textarea area with autocomplete overlays */}
          <div className="relative" style={{ padding: '12px 20px 0' }}>
            {/* Slash Command Autocomplete */}
            {showAutocomplete && (
              <CommandAutocomplete
                input={inputValue}
                onSelectCommand={handleSelectCommand}
                isVisible={showAutocomplete}
              />
            )}

            {/* Mention Autocomplete (@data, #page) */}
            {showMentionAutocomplete && activeMentionContext && (
              <MentionAutocomplete
                input={inputValue}
                cursorPosition={cursorPosition}
                onSelectMention={handleSelectMention}
                isVisible={showMentionAutocomplete}
                mentionType={activeMentionContext.type}
              />
            )}

            <Textarea
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={placeholder}
              disabled={isLoading}
              className="w-full resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 bg-transparent"
              rows={1}
              style={{
                fontSize: '14px',
                lineHeight: '1.5',
                minHeight: '24px',
                maxHeight: '200px',
                overflow: 'auto',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent',
              }}
            />
          </div>

          {/* Bottom toolbar */}
          <div
            className="flex items-center justify-between"
            style={{ padding: '4px 8px 8px 12px' }}
          >
            {/* Left group */}
            <div className="flex items-center gap-1">
              {/* + Button - placeholder for future file attachment */}
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full text-muted-foreground"
                      disabled={isLoading}
                      aria-label="Attach"
                    >
                      <Plus className="w-5 h-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">Attach</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Tools Button - Popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 rounded-full px-3 gap-1.5 text-muted-foreground"
                    aria-label="Tools menu"
                  >
                    <Wrench className="w-3.5 h-3.5" />
                    <span className="text-xs">Tools</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="w-52 p-1"
                  sideOffset={8}
                >
                  <div className="flex flex-col">
                    {/* Conversation History */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => {
                        setConversationPopoverOpen(true);
                      }}
                    >
                      <History className="w-4 h-4" />
                      Conversation History
                    </button>

                    {/* Slash Commands */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => {
                        setInputValue('/');
                        setTimeout(() => inputRef.current?.focus(), 50);
                      }}
                    >
                      <Terminal className="w-4 h-4" />
                      Slash Commands
                    </button>

                    {/* Skills */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => setShowSkillsModal(true)}
                    >
                      <Zap className="w-4 h-4" />
                      Skills
                    </button>

                    {/* Agents */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => setShowAgentsModal(true)}
                    >
                      <Bot className="w-4 h-4" />
                      Agents
                    </button>

                    {/* Knowledge */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => openKnowledgeModal()}
                    >
                      <BookOpen className="w-4 h-4" />
                      Knowledge
                    </button>

                    {/* Data */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => {
                        void dataDiscovery.openModal();
                      }}
                    >
                      <Database className="w-4 h-4" />
                      Data
                    </button>

                    {/* Workflows */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => window.open('/workflows', '_blank', 'noopener,noreferrer')}
                    >
                      <GitBranch className="w-4 h-4" />
                      Workflows
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </button>

                    {/* Missions */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => { mission.openModal(); }}
                    >
                      <Crosshair className="w-4 h-4" />
                      Missions
                    </button>

                    {/* Agent Debug Suite — platform admins only */}
                    {canUsePlatformAdminDebug && (
                      <button
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                        onClick={() => setShowDebugSuite(true)}
                      >
                        <Bug className="w-4 h-4" />
                        Agent Debug Suite
                      </button>
                    )}

                    {/* Style Intelligence */}
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                      onClick={() => { styleIntelligence.openModal(); }}
                    >
                      <Palette className="w-4 h-4" />
                      Style Intelligence
                    </button>

                  </div>
                </PopoverContent>
              </Popover>
              {/* Plan Mode Toggle */}
              <PlanModeToggle onOpenRoadmapModal={() => setRoadmapModalOpen(true)} />
              {activeStyleProfile && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => { styleIntelligence.openModal(); }}
                        className="h-8 inline-flex items-center gap-1.5 rounded-full px-3 text-xs font-medium text-primary bg-primary/10 border border-primary/20 hover:bg-primary/15 transition-colors"
                        aria-label={activeStyleAriaLabel}
                      >
                        <Palette className="w-3.5 h-3.5" />
                        <span className="max-w-[180px] truncate sm:max-w-[240px]">
                          {activeStyleLabel}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">{activeStyleTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Right group */}
            <div className="flex items-center gap-1">
              {/* Voice Mic - compact mode */}
              <VoiceMicButton
                voiceState={voiceState}
                onPress={handleMicPress}
                onStopSpeaking={handleStopSpeaking}
                disabled={isLoading}
                error={voiceError}
                compact
              />

              {/* Send / Stop button - circular */}
              <Button
                type="button"
                onClick={() => {
                  if (isLoading || isStreamingActive) {
                    handleStopGeneration();
                  } else {
                    handleSendMessage();
                  }
                }}
                disabled={!isLoading && !isStreamingActive && !inputValue.trim()}
                size="icon"
                className="h-8 w-8 rounded-full"
                aria-label={isLoading || isStreamingActive ? "Stop generation" : "Send message"}
                style={{
                  backgroundColor: isLoading || isStreamingActive
                    ? 'hsl(var(--destructive))'
                    : (!inputValue.trim() ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary))'),
                  color: isLoading || isStreamingActive ? 'hsl(var(--destructive-foreground))' : 'hsl(var(--primary-foreground))',
                  border: 'none',
                  transition: 'background-color 150ms ease-in-out',
                }}
              >
                {isLoading || isStreamingActive ? (
                  <Square className="w-3.5 h-3.5 fill-current" />
                ) : (
                  <ArrowUp className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
  );
};
